import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const IDLE_OPACITY = 0.42;
const HOVER_OPACITY = 1;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  return reduced;
}

export default function VehicleGalleryPreviewStrip({
  gallery,
  activeImageIndex,
  onSelect,
  isDark,
  photoLabel = "Photos",
}) {
  const railRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const copies = Math.max(3, Math.ceil(14 / Math.max(gallery.length, 1)));

  const loopItems = useMemo(
    () =>
      Array.from({ length: copies }, (_, copy) =>
        gallery.map((src, idx) => ({ src, idx, copy, key: `${copy}-${idx}-${src}` }))
      ).flat(),
    [gallery, copies]
  );

  const wrapScroll = useCallback((el) => {
    const width = el.scrollWidth / copies;
    if (width <= 0) return;
    if (el.scrollLeft >= width) el.scrollLeft -= width;
    else if (el.scrollLeft < 0) el.scrollLeft += width;
  }, [copies]);

  const scrollPreview = useCallback((direction) => {
    const el = railRef.current;
    if (!el) return;
    const width = el.scrollWidth / copies;
    const delta = Math.max(140, el.clientWidth * 0.55) * direction;
    let next = el.scrollLeft + delta;
    if (width > 0) {
      if (next < 0) next += width;
      if (next >= width) next -= width;
    }
    el.scrollTo({ left: next, behavior: hovered ? "smooth" : "auto" });
  }, [copies, hovered]);

  useEffect(() => {
    if (hovered || prefersReducedMotion || gallery.length < 2) return undefined;
    const el = railRef.current;
    if (!el) return undefined;
    let frame;
    const speed = 0.55;
    const step = () => {
      el.scrollLeft += speed;
      wrapScroll(el);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [hovered, prefersReducedMotion, gallery.length, wrapScroll]);

  useEffect(() => {
    if (!hovered) return undefined;
    const onKey = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const dir = event.key === "ArrowLeft" ? -1 : 1;
      scrollPreview(dir);
      const count = gallery.length;
      if (!count) return;
      onSelect((activeImageIndex + dir + count) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hovered, activeImageIndex, gallery.length, onSelect, scrollPreview]);

  useEffect(() => {
    if (!hovered) return;
    const rail = railRef.current;
    if (!rail) return;
    const thumbs = rail.querySelectorAll(`[data-preview-index="${activeImageIndex}"]`);
    if (!thumbs.length) return;
    const railRect = rail.getBoundingClientRect();
    let best = thumbs[0];
    let bestDist = Infinity;
    thumbs.forEach((thumb) => {
      const rect = thumb.getBoundingClientRect();
      const dist = Math.abs((rect.left + rect.right) / 2 - (railRect.left + railRect.right) / 2);
      if (dist < bestDist) {
        bestDist = dist;
        best = thumb;
      }
    });
    const rect = best.getBoundingClientRect();
    if (rect.left < railRect.left || rect.right > railRect.right) {
      rail.scrollTo({
        left: rail.scrollLeft + (rect.left - railRect.left) - (railRect.width - rect.width) / 2,
        behavior: "smooth",
      });
    }
  }, [activeImageIndex, hovered]);

  const arrowCls = isDark
    ? "bg-white/20 text-white hover:bg-white/30 hover:text-white"
    : "bg-gray-200 text-gray-900 hover:bg-gray-300 hover:text-gray-900";

  return (
    <div
      className="w-full max-w-full min-w-0 mt-3 px-0 py-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHovered(false);
      }}
    >
      <div className="flex items-center gap-2 w-full min-w-0 h-20">
        <button
          type="button"
          aria-label="Scroll preview photos left"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            scrollPreview(-1);
          }}
          className={`shrink-0 p-2 ${arrowCls}`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div
          ref={railRef}
          className="min-w-0 flex-1 h-20 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden"
          style={{
            scrollbarWidth: "none",
            opacity: hovered ? HOVER_OPACITY : IDLE_OPACITY,
            filter: hovered ? "blur(0px)" : "blur(2.4px)",
            transition: "opacity 220ms ease, filter 220ms ease",
            pointerEvents: hovered ? "auto" : "none",
          }}
        >
          <div className="flex items-center gap-2 h-20 w-max">
            {loopItems.map((item) => (
              <button
                key={item.key}
                type="button"
                data-preview-index={item.idx}
                aria-label={`${photoLabel} ${item.idx + 1}`}
                tabIndex={hovered ? 0 : -1}
                onClick={() => onSelect(item.idx)}
                className={`relative w-28 h-[4.5rem] overflow-hidden border-2 shadow-md shrink-0 ${
                  item.idx === activeImageIndex
                    ? isDark
                      ? "border-blue-500 ring-1 ring-blue-500/50"
                      : "border-red-600 ring-1 ring-red-600/40"
                    : isDark
                      ? "border-white/40"
                      : "border-gray-300"
                }`}
              >
                <img src={item.src} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          aria-label="Scroll preview photos right"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            scrollPreview(1);
          }}
          className={`shrink-0 p-2 ${arrowCls}`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
